<template>
	<div class="widget-builder sps-builder">
		<AppLoader :visible="isLoading" />

		<v-card class="filters-card" flat>
			<v-card-title>
				<UserViewControl :view-key="'SpsBuilder'" :get-view-state="getUserViewState" :apply-view-state="applyUserViewState" :default-view-state="getDefaultUserViewState" :is-ready="isUserViewReady" :state-version="viewStateVersion" @view-applied="onUserViewApplied">
					<template #actions>
						<v-badge :model-value="activeFilterCount > 0" :content="activeFilterCount" color="primary">
							<v-btn icon="filter_alt" variant="tonal" color="primary" density="comfortable" :disabled="!isUserViewReady" @click="isFilterSheetOpen = true" />
						</v-badge>
					</template>
				</UserViewControl>

				<FilterChips :chips="selectedFilterChips" @select="onFilterChipClick" @remove="removeFilterChip" @clear="resetFilters" />
			</v-card-title>

			<FilterControls ref="filterControls" :filters="filterDefinitions" :mobile="isLaptop" layout="panel" v-model:open="isFilterSheetOpen" @apply="onFiltersApply" @reset="resetFilters" @update:active-count="activeFilterCount = $event" />
		</v-card>

		<v-expansion-panels v-model="kpiPanel">
			<v-expansion-panel value="sps-list" class="widget-builder--panel" eager>
				<v-expansion-panel-title>
					<v-icon>store</v-icon>
					{{ title }}
					<v-badge color="primary" :content="displayCustoms.length + 1" inline></v-badge>
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<transition-group name="pop" tag="div" class="kpi-list">
						<div class="main-kpi" key="main-sps">
							<Kpi :title="title" color="primary" :width="12" :params="{ type: 'sps', shape: 'circle' }" :static-data="globalOverview" :annotation="t('KpiGlobal')" :need-title="false" :show-difference="true" @kpi-apply-filters="onGaugeSelected" />
						</div>
						<div class="custom-kpi" v-for="custom in displayCustoms" :key="custom.guid">
							<Kpi :title="title" color="primary" :width="12" :params="custom.params" :static-data="custom.staticData" :annotation="custom.annotation" :custom="true" :guid="custom.guid" :need-title="false" :show-difference="true" @kpi-apply-filters="onGaugeSelected" />
						</div>
						<div v-if="hasAppliedFilters" class="add-custom-kpi" key="add-sps">
							<v-tooltip :attach="overlayTarget" location="bottom">
								<template #activator="{ props: tooltipProps }">
									<v-btn v-bind="tooltipProps" icon="add_2" @click="createCustomSps" variant="tonal" color="primary" size="x-large" class="pulse"></v-btn>
								</template>
								<span>{{ t('AddNewKpi') }}</span>
							</v-tooltip>
						</div>
					</transition-group>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<KpiClusterPanel :clusters="clusterItems" />

		<DetailGridCard :title="title" :count="filteredRows.length" v-model:search="search">
			<template #groups>
				<QuickRowGroup v-if="!isLaptop" v-model="selectedGridRowGroups" :groups="quickRowGroupDefinitions" :default-active-groups="defaultActiveGridRowGroups" />
			</template>

			<div class="kpi-detail-card__grid-shell">
				<div v-show="filteredRows.length > 0" class="kpi-detail-card__grid">
					<AgGrid
						v-if="!isLaptop"
						ref="spsDetailGrid"
						:rowData="filteredRows"
						:getRowId="getRowId"
						:colDefs="columns"
						:getGroupRowAgg="getGroupRowAgg"
						:autoGroupColumnDef="spsDetailAutoGroupColumnDef"
						groupDisplayType="singleColumn"
						:sideBar="isTouchMode ? false : true"
						:row-group-panel-show="isTouchMode ? 'false' : 'always'"
						:pagination="true"
						:paginationPageSize="50"
						@grid-ready="onGridReady"
						@row-group-columns-changed="onGridRowGroupColumnsChanged"
						@view-state-changed="bumpViewStateVersion"
					/>

					<TouchCardData
						v-else
						:row-data="filteredRows"
						:columns="touchDetailColumns"
						title-field="deliveryLocation"
						title-delivery-location-id-field="deliveryLocationId"
						:subtitle-fields="['account', 'channel', 'deliveryLocationType']"
						row-key="id"
						metric-layout="rows"
						:groups="quickRowGroupDefinitions"
						v-model:group-columns="selectedGridRowGroups"
						:default-active-groups="defaultActiveGridRowGroups"
						:group-fields="rowGroupFieldByColId"
						:get-group-row-agg="getGroupRowAgg"
						:group-leaf-label="t('DeliveryLocations')"
						leaf-count-field="deliveryLocationId"
						sort-storage-key="spsDetail"
					/>
				</div>

				<v-sheet v-if="!isLoading && filteredRows.length === 0" class="kpi-detail-card__empty kpi-detail-card__empty--overlay" rounded>
					<v-icon>table_rows_narrow</v-icon>
					<span>{{ t('NoData') }}</span>
				</v-sheet>
			</div>
		</DetailGridCard>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, inject, nextTick, watch } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { buildFilterChips, scrollFilterFieldIntoView } from '@/utils/ui/filterChips';
import { eventBus } from '@/utils/core/eventBus';
import { buildDeliveryLocationAutoGroupColumnDef } from '@/utils/agGrid/agGridDeliveryLocationGroup';
import { spsService } from '@/services/spsService';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';

import Kpi from '@/components/kpi/Kpi.vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import TouchCardData from '@/components/agGrid/TouchCardData.vue';
import CompanyName from '@/components/common/CompanyName.vue';
import ProgressBarRenderer from '@/components/agGrid/ProgressBarRenderer.vue';
import FilterControls from '@/components/controls/FilterControls.vue';
import UserViewControl from '@/components/controls/UserViewControl.vue';
import FilterChips from '@/components/controls/FilterChips.vue';
import QuickRowGroup from '@/components/agGrid/QuickRowGroup.vue';
import AppLoader from '@/components/common/AppLoader.vue';
import KpiClusterPanel from '@/components/kpi/KpiClusterPanel.vue';
import DetailGridCard from '@/components/kpi/DetailGridCard.vue';

import { getOrganisationsTree, getOrganisationMaxLevel } from '@/api/core/organisation';
import { getAccounts } from '@/api/distributor/account';
import { getChannels } from '@/api/distributor/channel';

const SPS_CUSTOMS_SETTING_NAME = 'VueCustomSps';
const USER_VIEW_DEFAULT_ID = '__default__';

const GRID_GROUP_COLUMNS = {
	DELIVERY_LOCATION: 'rowGroupDeliveryLocation',
	ACCOUNT: 'rowGroupAccount',
	CHANNEL: 'rowGroupChannel',
	DELIVERY_LOCATION_TYPE: 'rowGroupDeliveryLocationType',
	LOCATION_GROUP: 'rowGroupLocationGroup',
};
const spsDetailAutoGroupColumnDef = buildDeliveryLocationAutoGroupColumnDef(GRID_GROUP_COLUMNS.DELIVERY_LOCATION, CompanyName);
const DEFAULT_DETAIL_GRID_ROW_GROUPS = [];
const DEFAULT_DETAIL_GRID_VISIBLE_COLUMNS = ['deliveryLocation', 'account', 'channel', 'deliveryLocationType', 'value', 'difference'];

const t = useTranslator();
const ecosVM = iframeDataBridge.getEcosVM();
const user = iframeDataBridge.getCurrentUser();
const overlayTarget = inject('overlayTarget', null);
const sortedWidgets = inject('sortedWidgets', ref([]));

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	params: {
		type: Object,
		default: () => ({}),
	},
	guid: {
		type: String,
		default: null,
	},
});

const isTouchMode = ref(deviceUtils.isTouchMode());
const isLaptop = ref(deviceUtils.isLaptop());
const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode();
	isLaptop.value = deviceUtils.isLaptop();
};

const organisations = ref([]);
const accounts = ref([]);
const channels = ref([]);
const deliveryLocationTypes = ref([]);

// Filter controls live inside FilterControls (side panel on laptop+, bottom sheet below) and are reached
// through its republished instance map, so every existing `xxxSelect.value?.getValues()` call still works.
const filterControls = ref(null);
const controlOf = (key) => computed(() => filterControls.value?.controls?.[key] || null);
const organisationTreeview = controlOf('organisationTreeview');
const accountSelect = controlOf('accountSelect');
const channelSelect = controlOf('channelSelect');
const deliveryLocationTypeSelect = controlOf('deliveryLocationTypeSelect');
const excludeSharedOrganisationsToggle = controlOf('excludeSharedOrganisationsToggle');
const isFilterSheetOpen = ref(false);
const activeFilterCount = ref(0);
const selectedFilterChips = ref([]);

// `section` buckets each field into one of the panel layout's fixed sections (see FilterControls.vue),
// with the same perimeter/point-of-sale split as KpiBuilder.
const filterDefinitions = computed(() => [
	{ key: 'organisationTreeview', type: 'treeview', label: t('Organisations'), items: organisations.value, titleKey: 'label', multiple: true, section: 'perimeter', branchSelection: true, selectAll: true, showSelectedCount: true, expandOnSearch: true, searchFields: ['title', 'reference'], itemsRegistration: 'props' },
	{ key: 'deliveryLocationTypeSelect', type: 'select', label: t('DeliveryLocationType'), items: deliveryLocationTypes.value, multiple: true, section: 'perimeter' },
	{ key: 'excludeSharedOrganisationsToggle', type: 'toggle', label: t('ExcludeSharedOrganisations'), section: 'perimeter' },
	{ key: 'accountSelect', type: 'select', label: t('Accounts'), items: accounts.value, multiple: true, section: 'pointOfSale' },
	{ key: 'channelSelect', type: 'select', label: t('Channels'), items: channels.value, multiple: true, section: 'pointOfSale' },
]);

const clusters = ref([]);
const rows = ref([]);
const search = ref('');
const isLoading = ref(true);
const spsDetailGrid = ref(null);
const lastKnownGridState = ref(null);
const organisationMaxLevel = ref(null);

const kpiPanel = ref('sps-list');
const hasAppliedFilters = ref(false);
const currentAppliedFilters = ref(null);
const userSettings = ref([]);
const customSps = ref([]);
const globalOverview = ref(emptyStaticData());

const selectedGridRowGroups = ref([...DEFAULT_DETAIL_GRID_ROW_GROUPS]);
const defaultActiveGridRowGroups = ref([...DEFAULT_DETAIL_GRID_ROW_GROUPS]);
const viewStateVersion = ref(0);
const isUserViewReady = ref(false);
const isGridReady = ref(false);
const gridReadyResolvers = [];

// Pinned SPS custom widgets carry the up-to-date (possibly renamed) title, so let a matching home widget
// override the catalog annotation, exactly like KpiBuilder's persisted-vs-local custom KPIs.
const displayCustoms = computed(() =>
	customSps.value.map((custom) => {
		const widget = Array.isArray(sortedWidgets?.value) ? sortedWidgets.value.find((w) => (w?.guid || w?.id) === custom.guid) : null;
		return { ...custom, annotation: widget?.title || custom.annotation };
	})
);

const clusterItems = computed(() =>
	clusters.value.map((cluster) => ({
		key: `cluster-${cluster.clusterId}`,
		title: props.title,
		annotation: cluster.clusterName,
		params: { type: 'sps', shape: 'circle', clusterId: cluster.clusterId },
		staticData: {
			value: cluster.value,
			formattedValue: cluster.formattedValue,
			percentage: cluster.percentage,
			difference: cluster.difference,
			formattedDifference: cluster.formattedDifference,
			classColor: cluster.classColor,
			color: 'primary',
			objective: '',
		},
	}))
);

const isOrgGroupingActive = computed(() => selectedGridRowGroups.value.some((colId) => colId.startsWith('rowGroupOrganisationLevel')));

const filteredRows = computed(() => {
	let result = rows.value;

	if (isOrgGroupingActive.value) {
		result = result.flatMap((row) => {
			if (!Array.isArray(row._organisationPaths)) {
				return [row];
			}
			return row._organisationPaths.map((orgLevels, index) => ({
				...row,
				...orgLevels,
				id: index === 0 ? row.id : `${row.id}__org${index}`,
			}));
		});
	}

	const searchValue = search.value?.trim().toLowerCase();
	if (!searchValue) {
		return result;
	}

	return result.filter((row) =>
		[row.deliveryLocation, row.account, row.channel, row.deliveryLocationType, row.locationGroup, row.city, row.postcode].some((value) =>
			String(value || '')
				.toLowerCase()
				.includes(searchValue)
		)
	);
});

const organisationRowGroupColumns = computed(() => {
	return Array.from({ length: organisationMaxLevel.value || 0 }, (_, index) => {
		const level = index + 1;
		return {
			colId: `rowGroupOrganisationLevel${level}`,
			field: `organisationLevel${level}`,
			headerName: `${t('Organisation')} ${t('Level')} ${level}`,
			hide: true,
			enableRowGroup: true,
			suppressColumnsToolPanel: true,
		};
	});
});

const quickRowGroupDefinitions = computed(() => {
	const groups = [
		{ key: 'deliveryLocation', label: t('DeliveryLocation'), columnField: GRID_GROUP_COLUMNS.DELIVERY_LOCATION },
		{ key: 'account', label: t('Account'), columnField: GRID_GROUP_COLUMNS.ACCOUNT },
		{ key: 'channel', label: t('Channel'), columnField: GRID_GROUP_COLUMNS.CHANNEL },
		{ key: 'deliveryLocationType', label: t('DeliveryLocationType'), columnField: GRID_GROUP_COLUMNS.DELIVERY_LOCATION_TYPE },
		{ key: 'locationGroup', label: t('Group'), columnField: GRID_GROUP_COLUMNS.LOCATION_GROUP },
	];

	if (organisationMaxLevel.value > 0) {
		groups.push({
			key: 'organisation',
			label: t('Organisation'),
			levels: Array.from({ length: organisationMaxLevel.value }, (_, index) => ({
				key: `organisation-level-${index + 1}`,
				label: `${t('Level')} ${index + 1}`,
				columnField: `rowGroupOrganisationLevel${index + 1}`,
			})),
		});
	}

	return groups;
});

const columns = computed(() => [
	{ colId: GRID_GROUP_COLUMNS.DELIVERY_LOCATION, field: 'deliveryLocation', headerName: t('DeliveryLocation'), hide: true, enableRowGroup: true, suppressColumnsToolPanel: true },
	{ colId: GRID_GROUP_COLUMNS.ACCOUNT, field: 'account', headerName: t('Account'), hide: true, enableRowGroup: true, suppressColumnsToolPanel: true },
	{ colId: GRID_GROUP_COLUMNS.CHANNEL, field: 'channel', headerName: t('Channel'), hide: true, enableRowGroup: true, suppressColumnsToolPanel: true },
	{ colId: GRID_GROUP_COLUMNS.DELIVERY_LOCATION_TYPE, field: 'deliveryLocationType', headerName: t('DeliveryLocationType'), hide: true, enableRowGroup: true, suppressColumnsToolPanel: true },
	{ colId: GRID_GROUP_COLUMNS.LOCATION_GROUP, field: 'locationGroup', headerName: t('Group'), hide: true, enableRowGroup: true, suppressColumnsToolPanel: true },
	...organisationRowGroupColumns.value,
	{ field: 'deliveryLocation', headerName: t('DeliveryLocation'), sortable: true, filter: true, resizable: true, minWidth: isTouchMode.value ? 150 : 220, cellRenderer: CompanyName, cellRendererParams: ({ data }) => ({ deliveryLocationId: data?.deliveryLocationId }) },
	{
		field: 'value',
		headerName: t('SPS'),
		sortable: true,
		filter: true,
		resizable: true,
		minWidth: isTouchMode.value ? 150 : 240,
		cellDataType: 'number',
		cellRenderer: ProgressBarRenderer,
		cellRendererParams: () => ({
			isPercentage: true,
			needLabel: false,
			hideObjective: true,
			barColor: 'primary',
			formatMetric: formatSps,
		}),
		valueFormatter: ({ value }) => formatSps(value),
		sort: 'desc',
	},
	{
		field: 'difference',
		headerName: t('Difference'),
		sortable: true,
		filter: true,
		resizable: true,
		maxWidth: 200,
		cellDataType: 'number',
		valueFormatter: ({ value }) => formatDifference(value),
		cellStyle: ({ value }) => getDifferenceCellStyle(value),
	},
	{ field: 'account', headerName: t('Account'), sortable: true, filter: true, resizable: true, minWidth: 160 },
	{ field: 'channel', headerName: t('Channel'), sortable: true, filter: true, resizable: true, minWidth: 140 },
	{ field: 'deliveryLocationType', headerName: t('DeliveryLocationType'), sortable: true, filter: true, resizable: true, minWidth: 160 },
	{ field: 'locationGroup', headerName: t('Group'), sortable: true, filter: true, resizable: true, minWidth: 140, hide: true },
	{ field: 'city', headerName: t('City'), sortable: true, filter: true, resizable: true, minWidth: 140, hide: true },
	...Array.from({ length: organisationMaxLevel.value || 0 }, (_, index) => ({
		field: `organisationLevel${index + 1}`,
		headerName: `${t('Organisation')} ${t('Level')} ${index + 1}`,
		sortable: true,
		filter: true,
		resizable: true,
		minWidth: 160,
		hide: true,
	})),
]);

// Maps ag-Grid row-group colIds to the underlying row fields so the mobile card list groups on the exact
// same columns QuickRowGroup drives on the grid. Derived from the colDefs, never hardcoded.
const rowGroupFieldByColId = computed(() =>
	columns.value.reduce((mapping, columnDef) => {
		if (typeof columnDef?.colId === 'string' && columnDef.colId.startsWith('rowGroup') && columnDef.field) {
			mapping[columnDef.colId] = columnDef.field;
		}
		return mapping;
	}, {})
);

const touchDetailColumns = computed(() => [{ field: 'value', label: t('SPS'), type: 'progress', colorField: 'color', isPercentage: true, hideObjective: true, barColor: 'primary', formatMetric: formatSps, deltaField: 'difference', deltaFormatMetric: formatDifference }]);

watch(selectedGridRowGroups, () => {
	applyDetailRowGrouping();
	bumpViewStateVersion();
});

// Crossing the breakpoint remounts the filter controls (panel <-> bottom sheet), so push the
// current selection back into the fresh instances.
watch(isLaptop, async () => {
	await nextTick();
	applyFiltersToControls(currentAppliedFilters.value || props.params);
});

watch(
	quickRowGroupDefinitions,
	(groups) => {
		const availableColumns = new Set(groups.flatMap((group) => (Array.isArray(group.levels) ? group.levels.map((level) => level.columnField) : [group.columnField])).filter(Boolean));
		const nextSelectedGroups = selectedGridRowGroups.value.filter((columnField) => availableColumns.has(columnField));
		if (nextSelectedGroups.length !== selectedGridRowGroups.value.length) {
			selectedGridRowGroups.value = nextSelectedGroups;
		}
	},
	{ immediate: true }
);

watch(columns, async () => {
	await nextTick();
	applyDetailRowGrouping();
});

onMounted(() => {
	eventBus.on('custom-kpi-removed', onCustomSpsRemoved);
	init();
	window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
	eventBus.off('custom-kpi-removed', onCustomSpsRemoved);
	window.removeEventListener('resize', onResize);
});

async function init() {
	isUserViewReady.value = false;
	await getFiltersData();
	await getRowGroupData();
	await loadPersistedCustomSps();
	globalOverview.value = await spsService.getSpsOverview({});
	await syncFiltersFromParams();
	await applyFilters();
	await nextTick();
	await waitForGridReady();
	hasAppliedFilters.value = true;
	isUserViewReady.value = true;
}

async function getFiltersData() {
	organisations.value = await getOrganisationsTree();
	accounts.value = await getAccounts();
	channels.value = await getChannels();
	deliveryLocationTypes.value = typeof ecosVM?.deliveryLocationTypes === 'function' ? ecosVM.deliveryLocationTypes() : ecosVM?.deliveryLocationTypes || [];
}

async function getRowGroupData() {
	organisationMaxLevel.value = await getOrganisationMaxLevel();
}

async function syncFiltersFromParams() {
	await nextTick();
	applyFiltersToControls(props.params || {});
}

function applyFiltersToControls(params = {}) {
	organisationTreeview.value?.selectItems(params.organisations || []);
	accountSelect.value?.selectItems(params.accounts || []);
	channelSelect.value?.selectItems(params.channels || []);
	deliveryLocationTypeSelect.value?.selectItems(params.deliveryLocationTypes || params.dlts || []);
	excludeSharedOrganisationsToggle.value?.selectItems(Boolean(params.excludeSharedOrganisations));
	filterControls.value?.refreshActiveCount();
}

function buildFilters() {
	return {
		organisations: organisationTreeview.value?.getValues() || [],
		accounts: accountSelect.value?.getValues() || [],
		channels: channelSelect.value?.getValues() || [],
		deliveryLocationTypes: deliveryLocationTypeSelect.value?.getValues() || [],
		excludeSharedOrganisations: Boolean(excludeSharedOrganisationsToggle.value?.getValues()),
	};
}

async function applyFilters() {
	const filters = buildFilters();
	currentAppliedFilters.value = { ...filters };
	updateSelectedFilterChips();
	isLoading.value = true;
	try {
		const report = await spsService.getSpsReport(filters);
		clusters.value = report.clusters;
		rows.value = report.rows;
	} finally {
		isLoading.value = false;
	}
	bumpViewStateVersion();
}

async function onFiltersApply() {
	isFilterSheetOpen.value = false;
	await applyFilters();
}

function resetFilters() {
	applyFiltersToControls({});
	applyFilters();
}

// Clicking a gauge (global or a saved custom) drives the detail grid + clusters with that gauge's filters.
async function onGaugeSelected(params = {}) {
	applyFiltersToControls(params);
	await nextTick();
	await applyFilters();
}

async function createCustomSps() {
	const filters = buildFilters();
	const params = { type: 'sps', shape: 'circle', organisations: filters.organisations, accounts: filters.accounts, channels: filters.channels, deliveryLocationTypes: filters.deliveryLocationTypes, excludeSharedOrganisations: filters.excludeSharedOrganisations };
	const custom = { guid: crypto.randomUUID(), annotation: buildCustomAnnotation(filters), params, staticData: emptyStaticData() };
	customSps.value.push(custom);
	await savePersistedCustomSps();
	const data = await spsService.getSpsOverview(filters);
	// Assign through the reactive array element (not the raw pushed object) so the gauge re-renders.
	const index = customSps.value.findIndex((item) => item.guid === custom.guid);
	if (index !== -1) customSps.value[index].staticData = data;
}

function onCustomSpsRemoved({ guid }) {
	if (!guid) return;
	const before = customSps.value.length;
	customSps.value = customSps.value.filter((custom) => custom.guid !== guid);
	if (customSps.value.length !== before) {
		savePersistedCustomSps();
	}
}

async function loadPersistedCustomSps() {
	userSettings.value = await getUserSettings({ groupName: 'WEBAPP', userId: user.id });
	const setting = userSettings.value.find((s) => s.name === SPS_CUSTOMS_SETTING_NAME);

	let stored = [];
	if (setting?.value) {
		try {
			const parsed = JSON.parse(setting.value);
			stored = Array.isArray(parsed) ? parsed : [];
		} catch {
			stored = [];
		}
	}

	customSps.value = stored.map((custom) => ({
		guid: custom.guid || crypto.randomUUID(),
		annotation: custom.annotation || t('SPS'),
		params: {
			type: 'sps',
			shape: 'circle',
			organisations: custom.params?.organisations || [],
			accounts: custom.params?.accounts || [],
			channels: custom.params?.channels || [],
			deliveryLocationTypes: custom.params?.deliveryLocationTypes || custom.params?.dlts || [],
			excludeSharedOrganisations: Boolean(custom.params?.excludeSharedOrganisations),
		},
		staticData: emptyStaticData(),
	}));

	await Promise.all(
		customSps.value.map(async (custom, index) => {
			const data = await spsService.getSpsOverview(custom.params);
			if (customSps.value[index]) customSps.value[index].staticData = data;
		})
	);
}

async function savePersistedCustomSps() {
	const stored = customSps.value.map((custom) => ({
		guid: custom.guid,
		annotation: custom.annotation,
		params: {
			organisations: custom.params.organisations || [],
			accounts: custom.params.accounts || [],
			channels: custom.params.channels || [],
			deliveryLocationTypes: custom.params.deliveryLocationTypes || [],
			excludeSharedOrganisations: Boolean(custom.params.excludeSharedOrganisations),
		},
	}));

	userSettings.value = userSettings.value.filter((s) => s.name !== SPS_CUSTOMS_SETTING_NAME);
	userSettings.value.push({ name: SPS_CUSTOMS_SETTING_NAME, groupName: 'WEBAPP', value: JSON.stringify(stored) });
	await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });
}

function buildCustomAnnotation(filters) {
	const parts = [];
	if (filters.organisations?.length) parts.push(filters.organisations.length === 1 ? organisationTreeview.value?.getFullValues()?.[0]?.label || t('Organisations') : `${t('Organisations')}: ${filters.organisations.length}`);
	if (filters.accounts?.length) parts.push(filters.accounts.length === 1 ? accountSelect.value?.getFullValues()?.[0]?.name || t('Accounts') : `${t('Accounts')}: ${filters.accounts.length}`);
	if (filters.channels?.length) parts.push(filters.channels.length === 1 ? channelSelect.value?.getFullValues()?.[0]?.name || t('Channels') : `${t('Channels')}: ${filters.channels.length}`);
	if (filters.deliveryLocationTypes?.length) parts.push(filters.deliveryLocationTypes.length === 1 ? deliveryLocationTypeSelect.value?.getFullValues()?.[0]?.name || t('DeliveryLocationType') : `${t('DeliveryLocationType')}: ${filters.deliveryLocationTypes.length}`);
	return parts.join(' • ') || t('SPS');
}

// `valueKey` per entry mirrors the control's own `titleKey` in filterDefinitions above.
function updateSelectedFilterChips() {
	selectedFilterChips.value = buildFilterChips([
		{ key: 'organisationTreeview', label: t('Organisations'), values: organisationTreeview.value?.getFullValues() || [], valueKey: 'label' },
		{ key: 'deliveryLocationTypeSelect', label: t('DeliveryLocationType'), values: deliveryLocationTypeSelect.value?.getFullValues() || [], valueKey: 'name' },
		{ key: 'excludeSharedOrganisationsToggle', label: t('ExcludeSharedOrganisations'), active: Boolean(excludeSharedOrganisationsToggle.value?.getValues()) },
		{ key: 'accountSelect', label: t('Accounts'), values: accountSelect.value?.getFullValues() || [], valueKey: 'name' },
		{ key: 'channelSelect', label: t('Channels'), values: channelSelect.value?.getFullValues() || [], valueKey: 'name' },
	]);
}

// Opens the panel/sheet and scrolls the matching field into view — distinct from the chip's own close
// (✕), which resets the whole underlying control.
async function onFilterChipClick(filterKey) {
	isFilterSheetOpen.value = true;
	await nextTick();
	setTimeout(() => scrollFilterFieldIntoView(filterKey, overlayTarget), 150);
}

async function removeFilterChip(filterKey) {
	const filterMap = {
		organisationTreeview,
		deliveryLocationTypeSelect,
		excludeSharedOrganisationsToggle,
		accountSelect,
		channelSelect,
	};
	const targetFilterRef = filterMap[filterKey]?.value;

	if (targetFilterRef && typeof targetFilterRef.reset === 'function') {
		targetFilterRef.reset();
	}

	filterControls.value?.refreshActiveCount();
	await applyFilters();
}

function emptyStaticData() {
	return { value: 0, formattedValue: '0', percentage: 0, difference: 0, formattedDifference: '0', classColor: '', color: 'primary', objective: '' };
}

// #region GRID + ROW GROUPING

function getRowId(params) {
	return String(params.data.id);
}

function getGroupRowAgg(params) {
	const leafRows = [];
	(params?.nodes || []).forEach((node) => {
		if (node?.data) {
			leafRows.push(node.data);
		} else if (Array.isArray(node?.allLeafChildren)) {
			node.allLeafChildren.forEach((leaf) => leaf?.data && leafRows.push(leaf.data));
		}
	});

	const average = (values) => (values.length ? Math.round((values.reduce((memo, value) => memo + value, 0) / values.length) * 10) / 10 : 0);

	return {
		value: average(leafRows.map((row) => Number(row?.value) || 0)),
		difference: average(leafRows.map((row) => Number(row?.difference) || 0)),
		color: 'primary',
	};
}

function onGridReady() {
	isGridReady.value = true;
	while (gridReadyResolvers.length > 0) {
		gridReadyResolvers.shift()?.();
	}
	applyDetailRowGrouping();
}

function onGridRowGroupColumnsChanged(colIds = []) {
	const normalized = [...new Set((Array.isArray(colIds) ? colIds : []).filter((colId) => typeof colId === 'string' && colId.length > 0))];
	if (haveSameItems(normalized, selectedGridRowGroups.value)) {
		return;
	}
	selectedGridRowGroups.value = normalized;
}

function applyDetailRowGrouping() {
	const grid = spsDetailGrid.value;
	if (!grid || typeof grid.setRowGroupColumns !== 'function') {
		return;
	}

	const api = typeof grid.getGridApi === 'function' ? grid.getGridApi() : null;
	const currentGroups =
		typeof api?.getRowGroupColumns === 'function'
			? api
					.getRowGroupColumns()
					.map((column) => column?.getColId?.())
					.filter(Boolean)
			: [];
	if (haveSameItems(currentGroups, selectedGridRowGroups.value)) {
		return;
	}

	grid.setRowGroupColumns(selectedGridRowGroups.value);
}

function haveSameItems(first = [], second = []) {
	if (first.length !== second.length) {
		return false;
	}
	const secondSet = new Set(second);
	return first.every((item) => secondSet.has(item));
}

async function waitForGridReady(timeoutMs = 2000) {
	// The grid is not rendered on mobile, so waiting would just burn the whole timeout during init().
	if (isLaptop.value) {
		return true;
	}

	if (isGridReady.value && typeof spsDetailGrid.value?.getGridApi === 'function' && spsDetailGrid.value.getGridApi()) {
		return true;
	}

	return await new Promise((resolve) => {
		const timeoutId = setTimeout(() => {
			const index = gridReadyResolvers.indexOf(onReady);
			if (index >= 0) gridReadyResolvers.splice(index, 1);
			resolve(false);
		}, timeoutMs);

		const onReady = () => {
			clearTimeout(timeoutId);
			resolve(true);
		};

		gridReadyResolvers.push(onReady);
	});
}

function bumpViewStateVersion() {
	viewStateVersion.value += 1;
}

// #endregion

// #region USER VIEWS

function getUserViewState() {
	return {
		filters: buildFilters(),
		quickGroups: [...selectedGridRowGroups.value],
		// On mobile the grid is not mounted: fall back to the last state we saw (or the one the applied view
		// carried) so saving a view from a phone never wipes its desktop column layout.
		gridState: getGridViewState() || lastKnownGridState.value,
	};
}

function getDefaultUserViewState() {
	return {
		filters: {},
		quickGroups: [...DEFAULT_DETAIL_GRID_ROW_GROUPS],
		gridState: buildDefaultGridState(),
	};
}

async function applyUserViewState(viewState = {}) {
	const filters = viewState?.filters || {};
	applyFiltersToControls(filters);
	if (viewState?.gridState) {
		lastKnownGridState.value = viewState.gridState;
	}

	selectedGridRowGroups.value = normalizeRowGroupColumns(Array.isArray(viewState?.quickGroups) ? viewState.quickGroups : DEFAULT_DETAIL_GRID_ROW_GROUPS);

	await applyFilters();
	await nextTick();
	await waitForGridReady();
	applyDetailRowGrouping();
	await nextTick();
	applyGridViewState(viewState?.gridState);
	bumpViewStateVersion();
}

function onUserViewApplied(view) {
	defaultActiveGridRowGroups.value = view?.id === USER_VIEW_DEFAULT_ID ? [...DEFAULT_DETAIL_GRID_ROW_GROUPS] : [];
}

function getGridViewState() {
	const gridState = typeof spsDetailGrid.value?.getViewState === 'function' ? spsDetailGrid.value.getViewState() : null;
	if (gridState) {
		lastKnownGridState.value = gridState;
	}
	return gridState;
}

function applyGridViewState(gridState) {
	if (gridState && typeof spsDetailGrid.value?.applyViewState === 'function') {
		spsDetailGrid.value.applyViewState(gridState, { preserveRowGroups: true });
	}
}

function normalizeRowGroupColumns(colIds = []) {
	const available = new Set(quickRowGroupDefinitions.value.flatMap((group) => (Array.isArray(group.levels) ? group.levels.map((level) => level.columnField) : [group.columnField])).filter(Boolean));
	return [...new Set((Array.isArray(colIds) ? colIds : []).filter((colId) => typeof colId === 'string' && available.has(colId)))];
}

function buildDefaultGridState() {
	const visibleColumnIds = new Set(DEFAULT_DETAIL_GRID_VISIBLE_COLUMNS);
	const groupColumnIds = new Set(DEFAULT_DETAIL_GRID_ROW_GROUPS);

	return {
		columnState: getLeafColumnIds(columns.value).map((colId) => ({
			colId,
			hide: !visibleColumnIds.has(colId),
			rowGroup: groupColumnIds.has(colId),
			rowGroupIndex: DEFAULT_DETAIL_GRID_ROW_GROUPS.indexOf(colId) >= 0 ? DEFAULT_DETAIL_GRID_ROW_GROUPS.indexOf(colId) : null,
		})),
		filterModel: {},
		rowGroupColumns: [...DEFAULT_DETAIL_GRID_ROW_GROUPS],
	};
}

function getLeafColumnIds(columnDefs = []) {
	return (Array.isArray(columnDefs) ? columnDefs : []).flatMap((columnDef) => {
		if (!columnDef || typeof columnDef !== 'object') {
			return [];
		}
		if (Array.isArray(columnDef.children) && columnDef.children.length > 0) {
			return getLeafColumnIds(columnDef.children);
		}
		const colId = columnDef.colId || columnDef.field;
		return typeof colId === 'string' && colId.length > 0 ? [colId] : [];
	});
}

// #endregion

function formatSps(value) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return '';
	return `${Math.round(numericValue * 10) / 10}`;
}

function formatDifference(value) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return '';
	const rounded = Math.round(numericValue * 10) / 10;
	return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

function getDifferenceCellStyle(value) {
	const numericValue = Number(value);
	if (numericValue > 0) return { color: 'rgb(var(--v-theme-success))', fontWeight: 600 };
	if (numericValue < 0) return { color: 'rgb(var(--v-theme-error))', fontWeight: 600 };
	return null;
}
</script>
